import { usePageLoading } from '@/components/layout/PageLoadingProvider'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  publicAsset,
} from '@/components/navigation/navigationAssets'
import {
  getOrCreateVoterId,
  getVotePriorityCategories,
  getVotePriorityResults,
  getVotePriorityStatus,
  submitVotePriority,
} from '@/lib/data/votePriority'
import type {
  VotePriorityCategory,
  VotePriorityResult,
} from '@/types/votePriority'
import { CityOpinion } from './CityOpinion'
import { PriorityOption } from './PriorityOption'
import { trackEvent } from '@/analytics'

const MAX_SELECTIONS = 5

interface VotePriorityProps {
  showCityOpinion?: boolean
  onResultsChange?: (results: VotePriorityResult[], totalResponses: number) => void
}

const categoryIcons = [
  ['seguridad', 'icons/election/seguridad.svg'],
  ['movilidad', 'icons/election/transporte.svg'],
  ['empleo', 'icons/election/trabajo.svg'],
  ['servicio', 'icons/election/servicios-basicos.svg'],
  ['obra', 'icons/election/obras-publicas.svg'],
  ['espacio', 'icons/election/espacio-publico.svg'],
  ['corrupcion', 'icons/election/corrupción.svg'],
  ['cultura', 'icons/election/cultura.svg'],
  ['educacion', 'icons/election/educacion.svg'],
]

function normalize(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function getCategoryIcon(category: VotePriorityCategory) {
  const searchable = normalize(`${category.id} ${category.label}`)
  const match = categoryIcons.find(([keyword]) => searchable.includes(normalize(keyword)))

  return match ? publicAsset(match[1]) : undefined
}

export function VotePriority({
  showCityOpinion = true,
  onResultsChange,
}: VotePriorityProps) {
  const [categories, setCategories] = useState<VotePriorityCategory[]>([])
  const [loadingInitialData, setLoadingInitialData] = useState(true)
  usePageLoading(loadingInitialData)
  const [selectedVotes, setSelectedVotes] = useState<string[]>([])
  const [results, setResults] = useState<VotePriorityResult[]>([])
  const [totalResponses, setTotalResponses] = useState(0)
  const [hasVoted, setHasVoted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSelectionDialogOpen, setIsSelectionDialogOpen] = useState(false)
  const [message, setMessage] = useState('')

  const voteGridRef = useRef<HTMLDivElement | null>(null)
  const voterId = useMemo(() => getOrCreateVoterId(), [])

  function updateResults(nextResults: VotePriorityResult[], nextTotalResponses = 0) {
    setResults(nextResults)
    setTotalResponses(nextTotalResponses)
    onResultsChange?.(nextResults, nextTotalResponses)
  }

  async function refreshResults() {
    const data = await getVotePriorityResults()
    updateResults(data.results ?? [], data.totalBallots ?? 0)
  }

  useEffect(() => {
    Promise.all([
      getVotePriorityCategories(),
      getVotePriorityStatus(voterId),
      getVotePriorityResults(),
    ])
      .then(([categoryData, statusData, resultData]) => {
        setCategories(categoryData.categories ?? [])
        setHasVoted(Boolean(statusData.hasVoted))
        updateResults(resultData.results ?? [], resultData.totalBallots ?? 0)
      })
      .catch((error) => setMessage(String(error.message || error)))
      .finally(() => setLoadingInitialData(false))
  }, [voterId])

  useEffect(() => {
    if (!isSelectionDialogOpen) return undefined

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !isSubmitting) {
        setIsSelectionDialogOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isSelectionDialogOpen, isSubmitting])

  function toggleVote(id: string) {
    if (hasVoted || isSubmitting) return

    if (selectedVotes.includes(id)) {
      const nextVotes = selectedVotes.filter((item) => item !== id)
      setSelectedVotes(nextVotes)
      setIsSelectionDialogOpen(nextVotes.length === MAX_SELECTIONS)
      trackEvent('vote_priority_selection', {
        event_category: 'participation',
        category_id: id,
        selection_action: 'remove',
        selected_count: nextVotes.length,
      })
      return
    }

    if (selectedVotes.length >= MAX_SELECTIONS) return

    const nextVotes = [...selectedVotes, id]
    setSelectedVotes(nextVotes)
    setIsSelectionDialogOpen(nextVotes.length === MAX_SELECTIONS)
    trackEvent('vote_priority_selection', {
      event_category: 'participation',
      category_id: id,
      selection_action: 'add',
      selected_count: nextVotes.length,
    })
    if (nextVotes.length === MAX_SELECTIONS) {
      trackEvent('vote_priority_review', {
        event_category: 'participation',
        selected_count: nextVotes.length,
      })
    }
  }

  function scrollVoteGrid(direction: -1 | 1) {
    const grid = voteGridRef.current

    if (!grid) return

    grid.scrollBy({
      left: direction * (grid.clientWidth * 0.8),
      behavior: 'smooth',
    })
  }

  async function handleSubmit() {
    if (selectedVotes.length < 1 || selectedVotes.length > MAX_SELECTIONS) return

    setIsSubmitting(true)
    setMessage('')

    try {
      const response = await submitVotePriority({
        voterId,
        votes: selectedVotes,
        source: 'home',
        userAgent: navigator.userAgent,
      })

      if (!response.ok) {
        trackEvent('vote_priority_submit', {
          event_category: 'participation',
          submission_status: response.error === 'already_voted' ? 'already_voted' : 'rejected',
          selected_count: selectedVotes.length,
        })
        if (response.error === 'already_voted') {
          setHasVoted(true)
          setMessage('Este navegador ya registró una participación.')
        } else {
          setMessage('No se pudo registrar la participación.')
        }
        return
      }

      setHasVoted(true)
      setIsSelectionDialogOpen(false)
      setMessage('Participación registrada.')
      trackEvent('vote_priority_submit', {
        event_category: 'participation',
        submission_status: 'success',
        selected_count: selectedVotes.length,
      })
      await refreshResults()
    } catch (error) {
      setMessage(String((error as Error).message || error))
      trackEvent('vote_priority_submit', {
        event_category: 'participation',
        submission_status: 'request_error',
        selected_count: selectedVotes.length,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedCategories = selectedVotes
    .map((id) => categories.find((category) => category.id === id))
    .filter((category): category is VotePriorityCategory => Boolean(category))

  return (
    <section className="vote-priority-card" aria-labelledby="vote-priority-title">
      <header>
        <div>
          <h2 className='leading-none' id="vote-priority-title">¿Qué tema debería atenderse primero en tu ciudad? </h2>
          <p> Participa en la encuesta.</p>
        </div>
        <div className="vote-priority-card__header-actions">
          <div className="vote-priority-card__header-nav" aria-label="Navegación de categorías">
            <button
              type="button"
              className="vote-priority-card__nav-button"
              aria-label="Ir a las categorías anteriores"
              onClick={() => scrollVoteGrid(-1)}
            >
              ←
            </button>
            <button
              type="button"
              className="vote-priority-card__nav-button"
              aria-label="Ir a las categorías siguientes"
              onClick={() => scrollVoteGrid(1)}
            >
              →
            </button>
          </div>
          <b aria-live="polite">
            {selectedVotes.length} / {MAX_SELECTIONS}
          </b>
        </div>
      </header>

      <div className="vote-grid" ref={voteGridRef}>
        {categories.map((category) => (
          <PriorityOption
            key={category.id}
            label={category.label}
            iconSrc={getCategoryIcon(category)}
            selected={selectedVotes.includes(category.id)}
            disabled={
              hasVoted ||
              isSubmitting ||
              (!selectedVotes.includes(category.id) &&
                selectedVotes.length >= MAX_SELECTIONS)
            }
            onToggle={() => toggleVote(category.id)}
          />
        ))}
      </div>

      {message ? <p className="vote-priority-card__status" role="status">{message}</p> : null}

      {showCityOpinion ? (
        <CityOpinion results={results} totalResponses={totalResponses} />
      ) : null}

      {isSelectionDialogOpen && !hasVoted ? (
        <div
          className="vote-priority-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="vote-priority-modal-title"
        >
          <div className="vote-priority-modal__panel">
            <div className="vote-priority-modal__copy">
              <p>Tu selección</p>
              <h3 id="vote-priority-modal-title">Confirma tus prioridades</h3>
              <span>
                Estas son tus {MAX_SELECTIONS} prioridades. Cada tema seleccionado suma un voto.
              </span>
            </div>

            <ul className="vote-priority-modal__list">
              {selectedCategories.map((category) => (
                <li key={category.id}>{category.label}</li>
              ))}
            </ul>

            <div className="vote-priority-modal__actions">
              <button
                type="button"
                className="vote-priority-modal__secondary"
                disabled={isSubmitting}
                onClick={() => setIsSelectionDialogOpen(false)}
              >
                Ajustar selección
              </button>
              <button
                type="button"
                className="vote-priority-modal__primary"
                disabled={
                  isSubmitting ||
                  selectedVotes.length < 1 ||
                  selectedVotes.length > MAX_SELECTIONS
                }
                onClick={handleSubmit}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar selección'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}
